import React, { useState, useEffect, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Avatar,
  Dialog,
  IconButton,
  Input,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { X, Upload } from "lucide-react";
import { io, Socket } from "socket.io-client";

import {
  getInAppNotificationsAPI,
  getFileByIdAPI,
  getPendingFollowRequestsAPI,
  handleFollowRequestAPI,
  markNotificationAsReadAPI,
  getUserProfile,
} from "../../../api/userApi";
import { sendMessageAPI } from "../../../api/messageApi";
import {
  acceptVideoRequestAPI,
  rejectVideoRequestAPI,
  setVideoRequestPendingAPI,
  uploadCompletedVideoAPI,
} from "../../../api/videoRequestsApi";
import { useAppSelector } from "../../../redux/typedHooks";
import { UserInterface } from "../../../types/user";
import { showToast } from "../../../utils/toast";
import {
  formatNotificationTime,
  formatNotificationTimeFull,
} from "../../../utils/timeFormatter";
import defaultAvatars from "../../../assets/defaultAvatars.svg";
import useNotificationsStyles from "./style";
import VerifiedShieldHelpIcon from "../../../assets/VerifiedShieldHelpIcon";
import notificationHeartIcon from "../../../assets/images/noficationHeartIcon.png";
import notificationHeartIconSvg from "../../../assets/images/notifcationHeartIcon.svg";
import messageIconSvg from "../../../assets/images/messageIcon.svg";
import subscribeNotificationIconSvg from "../../../assets/images/subscribeNotificationIcon.svg";
import notificationCoinIcon from "../../../assets/images/notificationCoint.png";
import notificationEllipseIcon from "../../../assets/images/notificationEllipse.png";
import notificationPlayIcon from "../../../assets/images/notificationPlayIcon.png";
import noNotificationsIcon from "../../../assets/images/icons/404NotificationMessage.png";
import RejectVideoRequestSuccessModal from "../../elements/RejectVideoRequestSuccessModal";
import { trackNotificationClicked } from "../../../analytics/canonicalTracking";

enum FollowStatus {
  NOT_FOLLOWING = "not_following",
  FOLLOWING = "following",
  PENDING = "pending",
}

interface InAppNotification {
  _id: string;
  type: string;
  message: string;
  isRead: boolean;
  createdDate: string;
  senderId?: string;
  senderUsername?: string;
  profileImg?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  follow?: {
    followUserId: string;
    accepted: boolean;
  } | null;
  isFollowing?: boolean;
  followStatus?: FollowStatus;
  repeaterMessage?: string;
  videoRequestId?: string;
  postId?: string;
  videoRequest?: {
    _id: string;
    requesterId: string;
    creatorId: string;
    conversationId: string;
    messageId: string;
    description: string;
    price: number;
    status: "pending" | "accepted" | "rejected" | "completed" | "cancelled";
    videoUrl?: string | null;
    paymentStatus: string;
    paymentId?: string | null;
    acceptedAt?: string | null;
    completedAt?: string | null;
    deadline?: string | null;
    rejectionReason?: string | null;
    estimatedDuration?: number;
    specialInstructions?: string;
    tags?: string[];
    createdAt: string;
    updatedAt: string;
  } | null;
  postIds?: string[];
}

interface FollowRequest {
  _id: string;
  requestId: string;
  senderId: string;
  firstName: string;
  lastName: string;
  username: string;
  profileImg?: string;
  createdAt: string;
  message: string;
}

interface InAppNotificationsProps {
  onNotificationRead?: () => void;
  refreshSignal?: number;
  /** Закриття панелі (наприклад Popover); показуємо кнопку лише на мобільних */
  onClose?: () => void;
}

const InAppNotifications: React.FC<InAppNotificationsProps> = ({
  onNotificationRead,
  refreshSignal,
  onClose,
}) => {
  const classes = useNotificationsStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
    defaultMatches:
      typeof window !== "undefined"
        ? window.innerWidth < theme.breakpoints.values.md
        : false,
  });
  const history = useHistory();
  const me = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const userId = me?.id;
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FollowRequest[]>([]);
  /** Одна панель — лише сповіщення (вкладки прибрані на всіх екранах) */
  const isNotificationsView = true;
  const isFollowRequestsView = false;
  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [page, setPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [hasMoreRequests, setHasMoreRequests] = useState(true);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {}
  );
  const fetchedUrlsRef = useRef<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const [hasFetchedNotificationsOnce, setHasFetchedNotificationsOnce] =
    useState(false);
  const [hasFetchedRequestsOnce, setHasFetchedRequestsOnce] = useState(false);
  const [fetchNotificationsTrigger, setFetchNotificationsTrigger] = useState(0);
  const [fetchRequestsTrigger] = useState(0);
  const loadMoreNotificationsRef = useRef<HTMLDivElement>(null);
  const loadMoreRequestsRef = useRef<HTMLDivElement>(null);
  const [videoRequestModalOpen, setVideoRequestModalOpen] = useState(false);
  const [selectedVideoRequest, setSelectedVideoRequest] =
    useState<InAppNotification | null>(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [deadline, setDeadline] = useState("");
  const [from, setFrom] = useState("");
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [rejectSuccessModalOpen, setRejectSuccessModalOpen] = useState(false);
  const videoFileInputRef = useRef<HTMLInputElement>(null);
  const [pullToRefreshState, setPullToRefreshState] = useState({
    isPulling: false,
    pullDistance: 0,
    isRefreshing: false,
  });
  const touchStartY = useRef<number>(0);
  const touchStartScrollTop = useRef<number>(0);
  /** Щоб не дублювати fetch при першому mount (isMobile-ефект) */
  const skipNextIsMobileLayoutEffect = useRef(true);

  // Функція для визначення статусу підписки (аналогічна CreatorSuggestions)
  const getFollowStatus = (
    follow: InAppNotification["follow"]
  ): FollowStatus => {
    if (!follow) {
      return FollowStatus.NOT_FOLLOWING;
    } else if (follow.accepted === true) {
      return FollowStatus.FOLLOWING;
    } else {
      return FollowStatus.PENDING;
    }
  };

  const getNotificationDateSectionLabel = (
    createdDate: string
  ): "Today" | "Yesterday" | "Earlier" => {
    const notificationDate = new Date(createdDate);
    if (Number.isNaN(notificationDate.getTime())) {
      return "Earlier";
    }

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (notificationDate >= startOfToday) {
      return "Today";
    }

    if (notificationDate >= startOfYesterday && notificationDate < startOfToday) {
      return "Yesterday";
    }

    return "Earlier";
  };

  // Функція для рефрешу нотифікацій
  const refreshNotifications = async () => {
    if (!me) return
    try {
      const response = await getInAppNotificationsAPI({
        page: 1,
        perPage: Math.max(notifications.length, 10),
      });

      if (response.success) {
        const updatedNotifications = response.data.items.map(
          (n: InAppNotification) => {
            return {
              ...n,
              isFollowing: n.follow?.accepted === true || false,
              followStatus: getFollowStatus(n.follow),
            };
          }
        );

        setNotifications(updatedNotifications);
      }
    } catch (err) {
      console.error("❌ Error refreshing notifications:", err);
    }
  };

  useEffect(() => {
    if (!refreshSignal) return;
    setPage(1);
    /** Не очищаємо список — інакше спалах «порожньо / завантаження» при відкритті поповера */
    refreshNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Function to enrich follow request with full user data if missing
  const enrichFollowRequest = async (
    request: FollowRequest
  ): Promise<FollowRequest> => {
    // Check if we have complete user data
    const hasCompleteData =
      request.firstName &&
      request.lastName &&
      request.username &&
      request.profileImg;

    if (hasCompleteData) {
      return request;
    }

    // If data is incomplete, fetch full user profile
    try {
      const userProfile = await getUserProfile(request.senderId);
      if (userProfile && userProfile._id) {
        return {
          ...request,
          firstName: request.firstName || userProfile.firstName || "",
          lastName: request.lastName || userProfile.lastName || "",
          username: request.username || userProfile.username || "",
          profileImg: request.profileImg || userProfile.profileImg || "",
        };
      }
    } catch (err) {
      console.error(
        `Error enriching follow request for user ${request.senderId}:`,
        err
      );
    }

    return request;
  };

  // Функція для рефрешу follow requests
  const refreshRequests = async () => {
    try {
      const response = await getPendingFollowRequestsAPI({
        page: 1,
        perPage: Math.max(pendingRequests.length, 10),
      });

      if (response.success) {
        // Enrich requests with full user data if missing
        const enrichedRequests = await Promise.all(
          response.data.items.map((r: FollowRequest) => enrichFollowRequest(r))
        );

        const updatedRequests = enrichedRequests.filter(
          (r: FollowRequest) => !pendingRequests.some(pr => pr._id === r._id)
        );
        setPendingRequests(updatedRequests);

        // Fetch media URLs for profile images
        enrichedRequests.forEach(
          (r: FollowRequest) => r.profileImg && fetchMediaUrl(r.profileImg)
        );
      }
    } catch (err) {
      console.error("❌ Error refreshing follow requests:", err);
    }
  };

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (
      !mediaUrl ||
      mediaUrl.includes("avatarViewer.png") ||
      mediaUrl.includes("defaultImage") ||
      mediaUrl.includes("defaultAvatars")
    ) {
      return defaultAvatars;
    }
    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      if (fetchedUrlsRef.current.has(mediaUrl)) {
        return mediaUrls[mediaUrl] || mediaUrl;
      }
      fetchedUrlsRef.current.add(mediaUrl);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      if (fetchedUrlsRef.current.has(mediaUrl)) {
        return mediaUrls[mediaUrl] || mediaUrl;
      }
      fetchedUrlsRef.current.add(mediaUrl);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    if (fetchedUrlsRef.current.has(mediaUrl)) {
      return mediaUrls[mediaUrl] || defaultAvatars;
    }
    fetchedUrlsRef.current.add(mediaUrl);
    if (!mediaUrl.startsWith("/api/files/")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
    try {
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId) throw new Error("Invalid file ID");
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      return defaultAvatars;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
    }
  };

  //    const fetchNotifications = async () => {
  //      setLoading(true);
  //      try {
  //        const response = await getInAppNotificationsAPI({ page, perPage: 10 });
  //            console.log("Notifications API Response:", response);
  //        if (response.success) {
  //                            const newNotifications = response.data.items
  //                    .map((n: InAppNotification) => ({
  //                        ...n,
  //                        isFollowing: n.follow?.accepted === true || false,
  //                        followStatus: getFollowStatus(n.follow),
  //                    }))
  //                    .filter((n: InAppNotification) => {
  //                        const existing = notifications.find((nn) => nn._id === n._id);
  //                        return !existing;
  //                    });
  //                setNotifications((prev) => (page === 1 ? newNotifications : [...prev, ...newNotifications]));
  //                setHasMoreNotifications(response.data.items.length === 10);
  //          response.data.items.forEach((notification: InAppNotification) => {
  //            if (notification.profileImg) {
  //              fetchMediaUrl(notification.profileImg);
  //            }
  //          });
  //        } else {
  //          setError(response.message || "Failed to fetch in-app notifications");
  //        }
  //      } catch (err) {
  //        setError("Something went wrong while fetching notifications");
  //        console.error(err);
  //      } finally {
  //        setLoading(false);
  //      }
  //    };

  useEffect(() => {
    if (skipNextIsMobileLayoutEffect.current) {
      skipNextIsMobileLayoutEffect.current = false;
      return;
    }
    setPage(1);
    setHasMoreNotifications(true);
    setFetchNotificationsTrigger(prev => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Load current user's avatar
  useEffect(() => {
    if (me?.profileImg) {
      fetchMediaUrl(me.profileImg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.profileImg]);

  useEffect(() => {
    if (!isNotificationsView || !hasMoreNotifications) return;

    const fetch = async () => {
      setLoading(true);
      try {
        const response = await getInAppNotificationsAPI({ page, perPage: 10 });
        if (response.success) {
          const mapped = response.data.items.map((n: InAppNotification) => ({
            ...n,
            isFollowing: n.follow?.accepted === true || false,
            followStatus: getFollowStatus(n.follow),
          }));
          if (page === 1) {
            // On the first page — always replace the list completely
            setNotifications(mapped);
          } else {
            // When loading — filter duplicates
            const newNotifications = mapped.filter(
              (n: InAppNotification) =>
                !notifications.some(nn => nn._id === n._id)
            );
            setNotifications(prev => [...prev, ...newNotifications]);
          }
          setHasMoreNotifications(response.data.items.length === 10);
          response.data.items.forEach(
            (n: InAppNotification) =>
              n.profileImg && fetchMediaUrl(n.profileImg)
          );
          setHasFetchedNotificationsOnce(true);
        } else {
          console.error(
            response.message || "Failed to fetch in-app notifications"
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, fetchNotificationsTrigger, isNotificationsView]);

  useEffect(() => {
    if (!isFollowRequestsView || !hasMoreRequests) return;

    const fetch = async () => {
      setLoadingRequests(true);
      try {
        const response = await getPendingFollowRequestsAPI({
          page: requestsPage,
          perPage: 10,
        });
        if (response.success) {
          // Enrich requests with full user data if missing
          const enrichedRequests = await Promise.all(
            response.data.items.map((r: FollowRequest) =>
              enrichFollowRequest(r)
            )
          );

          const newRequests = enrichedRequests.filter(
            (r: FollowRequest) => !pendingRequests.some(pr => pr._id === r._id)
          );
          setPendingRequests(prev =>
            requestsPage === 1 ? newRequests : [...prev, ...newRequests]
          );
          setHasMoreRequests(response.data.items.length === 10);
          // Fetch media URLs for profile images
          enrichedRequests.forEach(
            (r: FollowRequest) => r.profileImg && fetchMediaUrl(r.profileImg)
          );
          setHasFetchedRequestsOnce(true);
        } else {
          console.error(
            response.message || "Failed to fetch pending follow requests"
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingRequests(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestsPage, fetchRequestsTrigger, isFollowRequestsView]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });
    socketRef.current = socket;
    socket.on("connect", () => {
      socket.emit("register", userId);
    });
    socket.on("notification", (notification: InAppNotification) => {
      setNotifications(prev => {
        const existingIndex = prev.findIndex(
          n => n.senderId === notification.senderId
        );
        const updatedNotification = {
          ...notification,
          isFollowing: notification.follow?.accepted === true,
          followStatus: getFollowStatus(notification.follow),
        };
        if (existingIndex !== -1) {
          const updatedNotifications = [...prev];
          updatedNotifications[existingIndex] = updatedNotification;
          return updatedNotifications;
        }
        return [updatedNotification, ...prev];
      });
      if (notification.profileImg) {
        fetchMediaUrl(notification.profileImg);
      }
      if (onNotificationRead) {
        onNotificationRead();
      }
    });
    socket.on("connect_error", err => {
      console.error("WebSocket connection error:", err.message);
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId, onNotificationRead]);

  useEffect(() => {
    return () => {
      Object.values(mediaUrls).forEach(url => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      setMediaUrls({});
      setLoadingMedia({});
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchedUrlsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFollowRequest = async (
    requestId: string,
    action: "accept" | "reject"
  ) => {
    try {
      const response = await handleFollowRequestAPI({ requestId, action });
      if (response.success) {
        setPendingRequests(prev => prev.filter(r => r.requestId !== requestId));
        showToast(response.message, "success");
      } else {
        showToast(
          response.message || `Failed to ${action} follow request`,
          "error"
        );
      }
    } catch (err) {
      showToast(`Error while ${action}ing follow request`, "error");
      console.error(`Error ${action}ing follow request:`, err);
    }
  };

  // Mark notification as read (automatically archives it on backend)
  const markNotificationAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsReadAPI(notificationId);
      // Remove notification from list (it's automatically archived on backend)
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      // Call callback if provided
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Don't show error to user, just log it
    }
  };

  // Handle video request approve/reject - open modal
  const handleVideoRequestAction = (
    notification: InAppNotification,
    action: "accept" | "reject"
  ) => {
    const requestId =
      notification.videoRequest?._id ||
      notification.videoRequestId ||
      notification._id;

    setSelectedVideoRequest(notification);
    setRequestMessage(
      notification.videoRequest?.description || notification.message || ""
    );
    // Set deadline as createdAt + 7 days
    let deadlineDate = "";
    if (notification.videoRequest?.createdAt) {
      const createdAt = new Date(notification.videoRequest.createdAt);
      const deadline = new Date(createdAt);
      deadline.setDate(deadline.getDate() + 7);
      deadlineDate = deadline.toISOString().split("T")[0];
    } else if (notification.createdDate) {
      const createdAt = new Date(notification.createdDate);
      const deadline = new Date(createdAt);
      deadline.setDate(deadline.getDate() + 7);
      deadlineDate = deadline.toISOString().split("T")[0];
    }
    setDeadline(deadlineDate);
    setFrom(
      notification.firstName && notification.lastName
        ? `${notification.firstName} ${notification.lastName}`
        : notification.senderId || ""
    );
    setSelectedVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoRequestModalOpen(true);

    // Add requestId to URL params
    const url = new URL(window.location.href);
    url.searchParams.set("requestId", requestId);
    window.history.pushState({}, "", url.toString());
  };

  // Handle video upload
  const handleUploadVideo = async () => {
    if (!selectedVideoRequest) {
      showToast("Invalid video request", "error");
      return;
    }

    const requestId =
      selectedVideoRequest.videoRequest?._id ||
      selectedVideoRequest.videoRequestId ||
      selectedVideoRequest._id;

    if (!requestId) {
      showToast("Invalid video request", "error");
      return;
    }

    if (!selectedVideoFile) {
      showToast("Please select a video file", "error");
      return;
    }

    try {
      // First accept the request
      await acceptVideoRequestAPI(requestId);

      // Then upload the video
      const uploadResponse = await uploadCompletedVideoAPI(
        requestId,
        selectedVideoFile
      );

      if (!uploadResponse.success) {
        const errorMessage =
          uploadResponse.error ||
          uploadResponse.message ||
          "Failed to upload video";
        throw new Error(errorMessage);
      }

      showToast("Video uploaded successfully!", "success");
      setVideoRequestModalOpen(false);
      // Remove requestId from URL params
      const url = new URL(window.location.href);
      url.searchParams.delete("requestId");
      window.history.pushState({}, "", url.toString());
      await markNotificationAsRead(selectedVideoRequest._id);
    } catch (err: any) {
      showToast(err?.message || "Failed to upload video", "error");
      console.error("Error uploading video:", err);
    }
  };

  // Handle reject from modal
  const handleRejectFromModal = async () => {
    if (!selectedVideoRequest) {
      showToast("Invalid video request", "error");
      return;
    }

    const requestId =
      selectedVideoRequest.videoRequest?._id ||
      selectedVideoRequest.videoRequestId ||
      selectedVideoRequest._id;

    if (!requestId) {
      showToast("Invalid video request", "error");
      return;
    }

    try {
      const response = await rejectVideoRequestAPI(requestId);
      if (response && !response.success) {
        // Show error message from backend
        const errorMessage =
          response.message || "Failed to reject video request";
        showToast(errorMessage, "error");
        return;
      }
      setVideoRequestModalOpen(false);
      // Remove requestId from URL params
      const url = new URL(window.location.href);
      url.searchParams.delete("requestId");
      window.history.pushState({}, "", url.toString());
      await markNotificationAsRead(selectedVideoRequest._id);
      // Send notification message to conversation (recipient = requester)
      const conversationId = selectedVideoRequest.videoRequest?.conversationId;
      const recipientId =
        selectedVideoRequest.senderId ||
        selectedVideoRequest.videoRequest?.requesterId;
      if (conversationId && recipientId) {
        try {
          await sendMessageAPI(
            {
              conversationId,
              recipientId,
              content: "✕ You rejected custom video request",
              type: "text",
              isPaid: false,
              price: 0,
            },
            []
          );
        } catch (sendErr) {
          console.error("Error sending rejection message:", sendErr);
        }
      }
      // Show success modal
      setRejectSuccessModalOpen(true);
    } catch (err: any) {
      // Extract error message from backend response
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reject video request";
      showToast(errorMessage, "error");
      console.error("Error rejecting video request:", err);
    }
  };

  const handleVideoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith("video/")) {
        setSelectedVideoFile(file);
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setVideoPreviewUrl(previewUrl);
      } else {
        showToast("Please select a video file", "error");
      }
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  // Filtering is now done on backend - no need for local filtering

  // // Періодичне оновлення нотифікацій кожні 5 секунд
  // useEffect(() => {
  //   if (activeTab !== 0) return;
  //
  //   const interval = setInterval(() => {
  //       console.log('🔄 Periodic refresh (every 5 seconds)');
  //       refreshNotifications();
  //   }, 5000); // 5 секунд
  //
  //   return () => clearInterval(interval);
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [activeTab]);

  // Слухач для оновлення нотифікацій при фокусі на вікно
  useEffect(() => {
    const handleFocus = () => {
      if (isNotificationsView && notifications.length > 0) {
        refreshNotifications();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isNotificationsView) {
        refreshNotifications();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, isNotificationsView]);

  // Infinite scroll for notifications
  useEffect(() => {
    if (!isNotificationsView || !hasMoreNotifications || loading) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreNotifications && !loading) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreNotificationsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreNotifications, loading, isNotificationsView]);

  // Infinite scroll for follow requests
  useEffect(() => {
    if (!isFollowRequestsView || !hasMoreRequests || loadingRequests) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreRequests && !loadingRequests) {
          setRequestsPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRequestsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreRequests, loadingRequests, isFollowRequestsView]);

  // Prevent body scroll when scrolling inside notification popup
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // If scrolling up at top or scrolling down at bottom, allow propagation
    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
      return;
    }

    // Prevent scroll from propagating to body
    e.stopPropagation();
  };

  // Pull-to-refresh handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    touchStartY.current = e.touches[0].clientY;
    touchStartScrollTop.current = target.scrollTop;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // Pull-to-refresh logic
    if (isAtTop && touchStartScrollTop.current === 0) {
      const touchY = e.touches[0].clientY;
      const pullDistance = touchY - touchStartY.current;

      if (pullDistance > 0 && !pullToRefreshState.isRefreshing) {
        // User is pulling down
        const maxPullDistance = 100;
        const normalizedDistance = Math.min(pullDistance, maxPullDistance);

        setPullToRefreshState({
          isPulling: true,
          pullDistance: normalizedDistance,
          isRefreshing: false,
        });

        // Prevent default scrolling when pulling down
        if (pullDistance > 10) {
          e.preventDefault();
        }
      } else if (pullDistance <= 0) {
        // User released or pulled up
        setPullToRefreshState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: false,
        });
      }
    }

    // If at boundaries, allow default behavior (which will scroll body)
    if (isAtTop || isAtBottom) {
      return;
    }

    // Prevent touch scroll from propagating to body
    e.stopPropagation();
  };

  const handleTouchEnd = async (e: React.TouchEvent<HTMLDivElement>) => {
    const { pullDistance, isPulling } = pullToRefreshState;
    const pullThreshold = 50;

    if (
      isPulling &&
      pullDistance >= pullThreshold &&
      !pullToRefreshState.isRefreshing
    ) {
      // Trigger refresh
      setPullToRefreshState({
        isPulling: false,
        pullDistance: 0,
        isRefreshing: true,
      });

      try {
        if (isNotificationsView) {
          await refreshNotifications();
        } else if (isFollowRequestsView) {
          await refreshRequests();
        }
      } catch (err) {
        console.error("Error refreshing:", err);
      } finally {
        setPullToRefreshState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: false,
        });
      }
    } else {
      // Reset pull state
      setPullToRefreshState({
        isPulling: false,
        pullDistance: 0,
        isRefreshing: false,
      });
    }
  };

  return (
    <Box
      className={classes.notificationContainer}
      style={{ position: "relative" }}
    >
      {/* Pull-to-refresh indicator - positioned above tabs */}
      {pullToRefreshState.isPulling || pullToRefreshState.isRefreshing ? (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: `${Math.min(pullToRefreshState.pullDistance, 100) + 40}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backgroundColor: "#131313",
            paddingTop: "40px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
            borderRadius: "16px 16px 0 0",
            transform: `translateY(${-Math.min(pullToRefreshState.pullDistance, 100)}px)`,
            transition: pullToRefreshState.isRefreshing
              ? "transform 0.3s ease"
              : "none",
          }}
        >
          {pullToRefreshState.isRefreshing ? (
            <CircularProgress
              size={40}
              style={{ color: "#FE5A97" }}
              thickness={4}
            />
          ) : (
            <Typography
              style={{
                color: "#FFFFFF",
                fontSize: "16px",
                fontWeight: 600,
                opacity: Math.min(pullToRefreshState.pullDistance / 50, 1),
              }}
            >
              {pullToRefreshState.pullDistance >= 50
                ? "Release to refresh"
                : "Pull to refresh"}
            </Typography>
          )}
        </Box>
      ) : null}
      <Box className={classes.notificationsHeaderRow}>
        <Typography
          component="h2"
          variant="h6"
          className={`${classes.desktopNotificationsTitle} ${classes.notificationsTitleInHeader}`}
        >
          Notifications
        </Typography>
        {isMobile && onClose ? (
          <IconButton
            onClick={onClose}
            aria-label="Close notifications"
            className={classes.notificationsCloseButton}
            edge="end"
          >
            <X size={22} />
          </IconButton>
        ) : null}
      </Box>

      {isNotificationsView && (
        <>
          {loading &&
            notifications.length === 0 &&
            !hasFetchedNotificationsOnce ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={4}
              minHeight="200px"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                style={{ gap: "16px" }}
              >
                <CircularProgress size={32} style={{ color: "#FE5A97" }} />
                <Typography
                  variant="body2"
                  style={{
                    color: "#A3A3A3",
                    fontSize: "14px",
                  }}
                >
                  Loading notifications...
                </Typography>
              </Box>
            </Box>
          ) : !loading &&
            hasFetchedNotificationsOnce &&
            notifications.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ padding: "60px 40px" }}
            >
              <img
                src={noNotificationsIcon}
                alt="No notifications"
                style={{
                  width: "120px",
                  height: "auto",
                  marginBottom: "20px",
                }}
              />
              <Typography
                style={{
                  fontSize: "20px",
                  fontWeight: 400,
                  color: "#FFFFFF",
                }}
              >
                No new notifications
              </Typography>
            </Box>
          ) : (
            <Box
              className={classes.listContainer}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                position: "relative",
              }}
            >
              <List
                dense
                style={{
                  transform:
                    pullToRefreshState.isPulling ||
                      pullToRefreshState.isRefreshing
                      ? `translateY(${Math.min(pullToRefreshState.pullDistance * 0.5, 50)}px)`
                      : "translateY(0px)",
                  transition: pullToRefreshState.isRefreshing
                    ? "transform 0.3s ease"
                    : "none",
                }}
              >
                {notifications.map((notification, index) => {
                  const sectionLabel = getNotificationDateSectionLabel(
                    notification.createdDate
                  );
                  const previousSectionLabel =
                    index > 0
                      ? getNotificationDateSectionLabel(
                        notifications[index - 1].createdDate
                      )
                      : null;
                  const shouldRenderSectionHeader =
                    index === 0 || sectionLabel !== previousSectionLabel;

                  // System notification types (no senderId, no follow button)
                  const systemNotificationTypes = [
                    "dmReminder",
                    "profileCompletion",
                    "dmQuotaExhausted",
                    "firstLogin",
                    "followerMilestone",
                    "unreadReminder",
                  ];
                  // Message notification types (link to messages, no follow button)
                  const messageNotificationTypes = [
                    "dmActivity",
                    "messageMention",
                    "messageReply",
                    "newMessage",
                  ];
                  const hasFirstAndLastName = Boolean(
                    notification.firstName && notification.lastName
                  );
                  const ownProfileAvatarNotificationTypes = [
                    "newDeviceLogin",
                    "subscriptionExpired",
                    "profileEngagement",
                    "unreadReminder",
                    "monthlyPerformance",
                  ];
                  const isSystemNotification = systemNotificationTypes.includes(
                    notification.type
                  );
                  const isOwnProfileAvatarNotification =
                    ownProfileAvatarNotificationTypes.includes(notification.type);
                  const isMessageNotification =
                    messageNotificationTypes.includes(notification.type);
                  const isNewMessage = notification.type === "newMessage";
                  const isMessageReply = notification.type === "messageReply";
                  const isWeeklyTrending = notification.type === "topPostsDigest"
                  const isPlatformUpdates = notification.type === "platformUpdates"
                  // Check if this is a "posted new content for subscribers" notification
                  const isSubscriberContentNotification =
                    notification.type === "subscriberContent" ||
                    notification.type === "newSubscriberContent";
                  // Check if this is a credits purchase notification
                  const isCreditsNotification =
                    notification.type === "earningSummary " ||
                    notification.type === "dailyUpdates" ||
                    notification.type === "weeklyUpdates" ||
                    notification.type === "monthlyUpdates" ||
                    notification.type === "weeklyEarnings" ||
                    notification.type === "payoutProcessed";
                  const isDmReminder = notification.type === "dmReminder";
                  const isProfileCompletion =
                    notification.type === "profileCompletion";
                  const isDmQuotaExhausted =
                    notification.type === "dmQuotaExhausted";
                  const isFirstLogin = notification.type === "firstLogin";
                  const isFollowerMilestone =
                    notification.type === "followerMilestone";
                  const isUnreadReminder =
                    notification.type === "unreadReminder";

                  // Check if this is a "liked your post" notification
                  const isPostLikeNotification =
                    notification.message
                      ?.toLowerCase()
                      .includes("liked your post") ||
                    notification.repeaterMessage
                      ?.toLowerCase()
                      .includes("liked your post") ||
                    notification.type === "postLike";

                  // Check if this is a video request notification
                  const isVideoRequestNotification =
                    notification.type === "videoRequestRejected" ||
                    notification.type === "videoRequestAccepted" ||
                    notification.type === "videoRequestSend";

                  const handleNotificationClick = () => {
                    // Navigate based on notification type
                    if (isDmReminder || isDmQuotaExhausted) {
                      history.push("/subscriptions");
                    } else if (isProfileCompletion) {
                      history.push("/choose-user-type");
                    } else if (isMessageNotification) {
                      // Message notifications link to messages page
                      history.push("/messages");
                    }
                    // Other system notifications might not need navigation
                  };

                  // Handle navigation to content (post) or profile
                  const handleContentClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    // For post like notifications, navigate to the post creator's profile (current user)
                    if (isPostLikeNotification && me?.username) {
                      const scrollPosition = window.scrollY;
                      sessionStorage.setItem(
                        "myFeedScrollPosition",
                        scrollPosition.toString()
                      );
                      if (notification.postId) {
                        history.push({
                          pathname: `/${me.username}`,
                          search: `?postId=${notification.postId}`,
                          state: {
                            fromNotification: true,
                            postId: notification.postId,
                            previousPath: history.location.pathname,
                          },
                        });
                      } else {
                        history.push(`/${me.username}`);
                      }
                    } else if (notification.postId && notification.senderId) {
                      // If there's a postId, navigate to the post
                      const scrollPosition = window.scrollY;
                      sessionStorage.setItem(
                        "myFeedScrollPosition",
                        scrollPosition.toString()
                      );
                      history.push({
                        pathname: `/${notification.senderUsername}`,
                        search: `?postId=${notification.postId}`,
                        state: {
                          fromNotification: true,
                          postId: notification.postId,
                          previousPath: history.location.pathname,
                        }
                      });
                    } else if (notification.senderId) {
                      // Otherwise navigate to profile
                      history.push({
                        pathname: `/${notification.senderUsername}`,
                        state: {
                          fromNotification: true,
                          previousPath: history.location.pathname,
                        }
                      });
                    }
                    if (onClose) onClose();
                  };

                  // Handle profile click (avatar or username) - always navigate to profile
                  const handleProfileClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    // Let the Link handle navigation naturally, but ensure we navigate to profile
                    // The Link's 'to' prop already points to profile, so navigation will happen correctly
                    if (notification.senderId) {
                      // Use history.push to ensure consistent navigation behavior
                      history.push(
                        `/${notification.username || notification.senderUsername}`
                      );
                      onClose && onClose()
                    }
                  };

                  // Get icon and color for system notifications
                  const getSystemNotificationIcon = () => {
                    if (isDmReminder)
                      return { image: notificationHeartIcon, color: "#f06ec4" };
                    if (isUnreadReminder)
                      return {
                        image: notificationEllipseIcon,
                        color: "#f06ec4",
                      };
                    if (isProfileCompletion)
                      return { emoji: "📝", color: "#4CAF50" };
                    if (isDmQuotaExhausted)
                      return { emoji: "⚠️", color: "#FF9800" };
                    if (isFirstLogin) return { emoji: "👋", color: "#2196F3" };
                    if (isFollowerMilestone)
                      return { emoji: "🎉", color: "#9C27B0" };
                    return { emoji: "🔔", color: "#757575" };
                  };

                  // Handle click for all notification types
                  const handleItemClick = async (e: React.MouseEvent) => {
                    // Don't mark as read if clicking on links or buttons
                    const target = e.target as HTMLElement;
                    if (
                      target.tagName === "A" ||
                      target.tagName === "BUTTON" ||
                      target.closest("a") ||
                      target.closest("button")
                    ) {
                      return;
                    }

                    trackNotificationClicked({
                      notification_type: notification.type || "unknown",
                      destination_type: notification.postId
                        ? "post"
                        : notification.senderId
                          ? "profile"
                          : "in_app",
                      notification_id: notification._id,
                      surface: "header_notifications_list",
                      mvp_feature: `notification_${notification.type || "unknown"}`,
                      user_id: userId,
                      post_id: notification.postId,
                      creator_id: notification.senderId,
                      user_role: me?.userType?.title,
                    });

                    // Do not mark as read on click to keep notifications visible after refresh.

                    // Navigate for system and message notifications
                    if (isSystemNotification || isMessageNotification) {
                      handleNotificationClick();
                    } else if (
                      notification.type === "followRequest" ||
                      notification.type === "newFollower"
                    ) {
                      // For follow request notifications, navigate to profile
                      if (notification.senderId) {
                        history.push(
                          `/${notification.username || notification.senderUsername}`
                        );
                      }
                    } else if (
                      // Post-related notifications: navigate to the post
                      (notification.postId && notification.senderId) ||
                      isPostLikeNotification ||
                      isSubscriberContentNotification
                    ) {
                      // For post like notifications, navigate to the post creator's profile (current user)
                      if (isPostLikeNotification && me?.username) {
                        const scrollPosition = window.scrollY;
                        sessionStorage.setItem(
                          "myFeedScrollPosition",
                          scrollPosition.toString()
                        );
                        if (notification.postId) {
                          history.push({
                            pathname: `/${me.username}`,
                            search: `?postId=${notification.postId}`,
                            state: {
                              fromNotification: true,
                              postId: notification.postId,
                              previousPath: history.location.pathname,
                            },
                          });
                        } else {
                          history.push(`/${me.username}`);
                        }
                      } else if (notification.postId && notification.senderId) {
                        // Navigate to post if postId exists
                        const scrollPosition = window.scrollY;
                        sessionStorage.setItem(
                          "myFeedScrollPosition",
                          scrollPosition.toString()
                        );
                        history.push({
                          pathname: `/${notification.username || notification.senderUsername}`,
                          search: `?postId=${notification.postId}`,
                          state: {
                            fromNotification: true,
                            postId: notification.postId,
                            previousPath: history.location.pathname,
                          },
                        });
                      } else if (notification.senderId) {
                        // If no postId but senderId exists, navigate to profile
                        history.push(
                          `/${notification.username || notification.senderUsername}`
                        );
                      }
                    } else if (notification.senderId) {
                      // Otherwise navigate to profile
                      history.push(
                        `/${notification.username || notification.senderUsername}`
                      );
                    }
                  };

                  return (
                    <React.Fragment key={notification._id}>
                      {shouldRenderSectionHeader && (
                        <Typography className={classes.sectionHeader}>
                          {sectionLabel}
                        </Typography>
                      )}
                      <ListItem
                        className={classes.notificationItem}
                        onClick={handleItemClick}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {isOwnProfileAvatarNotification ? (
                          <Avatar
                            src={
                              loadingMedia[me?.profileImg || ""]
                                ? undefined
                                : mediaUrls[me?.profileImg || ""] || defaultAvatars
                            }
                            className={classes.avatar}
                            style={{ marginRight: 12 }}
                          >
                            {loadingMedia[me?.profileImg || ""] && (
                              <CircularProgress size={24} />
                            )}
                          </Avatar>
                        ) : isSystemNotification ? (
                          (() => {
                            const iconData = getSystemNotificationIcon();
                            return (
                              <Box
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: "50%",
                                  backgroundColor: iconData.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "20px",
                                  marginRight: 12,
                                  flexShrink: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {iconData.image ? (
                                  <img
                                    src={iconData.image}
                                    alt="notification icon"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  iconData.emoji
                                )}
                              </Box>
                            );
                          })()
                        ) : isPostLikeNotification &&
                          notification.senderId &&
                          me ? (
                          // Post like notification - show two overlapping avatars
                          <Box
                            style={{
                              position: "relative",
                              width: 44,
                              height: 44,
                              marginRight: 12,
                              flexShrink: 0,
                            }}
                          >
                            {/* User's avatar (bottom right, behind) */}
                            <Box
                              style={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                zIndex: 1,
                              }}
                            >
                              <Avatar
                                src={
                                  loadingMedia[me.profileImg || ""]
                                    ? undefined
                                    : mediaUrls[me.profileImg || ""] ||
                                    defaultAvatars
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  border: "2px solid #131313",
                                }}
                              >
                                {loadingMedia[me.profileImg || ""] && (
                                  <CircularProgress size={20} />
                                )}
                              </Avatar>
                              {/* Heart icon below user's avatar, on the right */}
                              <Box
                                style={{
                                  position: "absolute",
                                  bottom: -4,
                                  right: -4,
                                  width: 20,
                                  height: 20,
                                  zIndex: 3,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={notificationHeartIconSvg}
                                  alt="heart icon"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                  }}
                                />
                              </Box>
                            </Box>
                            {/* Liker's avatar (top left, in front) */}
                            <Link
                              to={`/${notification.senderId}`}
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleProfileClick(e);
                              }}
                            >
                              <Avatar
                                src={
                                  loadingMedia[notification.profileImg || ""]
                                    ? undefined
                                    : mediaUrls[notification.profileImg || ""] ||
                                    defaultAvatars
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  border: "2px solid #131313",
                                  zIndex: 2,
                                }}
                              >
                                {loadingMedia[notification.profileImg || ""] && (
                                  <CircularProgress size={20} />
                                )}
                              </Avatar>
                            </Link>
                          </Box>
                        ) : // Message notifications or follow notifications - show user avatar
                          notification.senderId ? (
                            <Box
                              style={{
                                position: "relative",
                                marginRight: 12,
                                flexShrink: 0,
                              }}
                            >
                              <Link
                                to={`/${notification.senderId}`}
                                style={{ textDecoration: "none", color: "inherit" }}
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  // Prevent navigation to profile if it's a message notification
                                  if (isMessageNotification) {
                                    handleNotificationClick();
                                  } else {
                                    handleProfileClick(e);
                                  }
                                }}
                              >
                                <Avatar
                                  src={
                                    loadingMedia[notification.profileImg || ""]
                                      ? undefined
                                      : mediaUrls[notification.profileImg || ""] ||
                                      defaultAvatars
                                  }
                                  className={classes.avatar}
                                >
                                  {loadingMedia[notification.profileImg || ""] && (
                                    <CircularProgress size={24} />
                                  )}
                                </Avatar>
                              </Link>
                              {/* Message icon for newMessage and messageReply types, bottom right */}
                              {(isNewMessage || isMessageReply) && (
                                <Box
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    zIndex: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={messageIconSvg}
                                    alt="message icon"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                </Box>
                              )}
                              {/* Subscribe icon for subscriber content notification, bottom right */}
                              {isSubscriberContentNotification && (
                                <Box
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    zIndex: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={subscribeNotificationIconSvg}
                                    alt="subscribe icon"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                </Box>
                              )}
                              {/* Coin icon for credits purchase notification, bottom right */}
                              {isCreditsNotification && (
                                <Box
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    zIndex: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={notificationCoinIcon}
                                    alt="credits icon"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                </Box>
                              )}
                              {/* Play icon for video request notifications, bottom right */}
                              {isVideoRequestNotification && (
                                <Box
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    zIndex: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={notificationPlayIcon}
                                    alt="video request icon"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          ) : (
                            // Fallback avatar if no senderId
                            <Avatar
                              src={defaultAvatars}
                              className={classes.avatar}
                              style={{ marginRight: 12 }}
                            />
                          )}
                        <ListItemText
                          primary={(() => {
                            if (isSystemNotification) {
                              // System notifications (dmReminder, profileCompletion, etc.)
                              return (
                                <>
                                  <Typography
                                    component="div"
                                    className={classes.body}
                                    onClick={handleContentClick}
                                    style={{
                                      cursor:
                                        notification.postId ||
                                          notification.senderId
                                          ? "pointer"
                                          : "default",
                                    }}
                                  >
                                    {notification.repeaterMessage ||
                                      notification.message}
                                  </Typography>
                                  <Tooltip
                                    title={formatNotificationTimeFull(
                                      notification.createdDate
                                    )}
                                    enterDelay={300}
                                    leaveDelay={0}
                                  >
                                    <Typography
                                      component="span"
                                      style={{
                                        fontSize: "12px",
                                        color: "#A3A3A3",
                                        fontWeight: 400,
                                        marginTop: 4,
                                        display: "block",
                                      }}
                                    >
                                      {formatNotificationTime(
                                        notification.createdDate
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            } else if (isMessageNotification) {
                              // Message notifications (dmActivity, messageMention, messageReply, newMessage)
                              return (
                                <>
                                  {notification.senderId ? (
                                    <Link
                                      to={`/${notification.senderId}`}
                                      style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                      }}
                                      onClick={e => {
                                        // Prevent navigation to profile, go to messages instead
                                        e.preventDefault();
                                        handleNotificationClick();
                                      }}
                                    >
                                      <Typography
                                        component="div"
                                        className={classes.headline}
                                        style={{ marginBottom: 4 }}
                                      >
                                        {notification.firstName &&
                                          notification.lastName
                                          ? `${notification.firstName} ${notification.lastName}`
                                          : notification.senderId || "User"}
                                        {hasFirstAndLastName && (
                                          <VerifiedShieldHelpIcon
                                            style={{ marginLeft: "1px" }}
                                            className={classes.verifiedIcon}
                                          />
                                        )}
                                      </Typography>
                                    </Link>
                                  ) : null}
                                  <Typography
                                    component="div"
                                    className={classes.body}
                                    onClick={handleContentClick}
                                    style={{
                                      cursor:
                                        notification.postId ||
                                          notification.senderId
                                          ? "pointer"
                                          : "default",
                                    }}
                                  >
                                    {notification.repeaterMessage ||
                                      notification.message}
                                  </Typography>
                                  <Tooltip
                                    title={formatNotificationTimeFull(
                                      notification.createdDate
                                    )}
                                    enterDelay={300}
                                    leaveDelay={0}
                                  >
                                    <Typography
                                      component="span"
                                      style={{
                                        fontSize: "12px",
                                        color: "#A3A3A3",
                                        fontWeight: 400,
                                        marginTop: 4,
                                        display: "block",
                                      }}
                                    >
                                      {formatNotificationTime(
                                        notification.createdDate
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            } else if (isSubscriberContentNotification) {
                              // Subscriber content notifications - parse name from message
                              const displayMessage =
                                notification.repeaterMessage ||
                                notification.message;
                              const fullName =
                                notification.firstName && notification.lastName
                                  ? `${notification.firstName} ${notification.lastName}`
                                  : notification.senderId || "";

                              // Find the name in the message and split
                              let nameStart = -1;
                              let nameEnd = -1;
                              if (fullName) {
                                nameStart = displayMessage.indexOf(fullName);
                                if (nameStart !== -1) {
                                  nameEnd = nameStart + fullName.length;
                                }
                              }

                              const beforeName =
                                nameStart !== -1
                                  ? displayMessage.substring(0, nameStart)
                                  : "";
                              const name =
                                nameStart !== -1
                                  ? displayMessage.substring(nameStart, nameEnd)
                                  : "";
                              const afterName =
                                nameEnd !== -1
                                  ? displayMessage.substring(nameEnd)
                                  : displayMessage;

                              return (
                                <>
                                  <Typography
                                    component="div"
                                    className={classes.body}
                                    onClick={handleContentClick}
                                    style={{
                                      cursor:
                                        notification.postId ||
                                          notification.senderId
                                          ? "pointer"
                                          : "default",
                                    }}
                                  >
                                    {beforeName}
                                    {name && notification.senderId ? (
                                      <Link
                                        to={`/${notification.senderId}`}
                                        style={{
                                          textDecoration: "none",
                                          color: "inherit",
                                        }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleProfileClick(e);
                                        }}
                                      >
                                        <span className={classes.headline}>
                                          {name}
                                          {hasFirstAndLastName && (
                                            <VerifiedShieldHelpIcon
                                              style={{
                                                marginLeft: "1px",
                                                display: "inline-block",
                                                verticalAlign: "middle",
                                              }}
                                              className={classes.verifiedIcon}
                                            />
                                          )}
                                        </span>
                                      </Link>
                                    ) : notification.senderId ? (
                                      <Link
                                        to={`/${notification.senderId}`}
                                        style={{
                                          textDecoration: "none",
                                          color: "inherit",
                                        }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleProfileClick(e);
                                        }}
                                      >
                                        <span className={classes.headline}>
                                          {fullName}
                                          {hasFirstAndLastName && (
                                            <VerifiedShieldHelpIcon
                                              style={{
                                                marginLeft: "1px",
                                                display: "inline-block",
                                                verticalAlign: "middle",
                                              }}
                                              className={classes.verifiedIcon}
                                            />
                                          )}
                                        </span>
                                      </Link>
                                    ) : null}
                                    {afterName}
                                  </Typography>
                                  <Tooltip
                                    title={formatNotificationTimeFull(
                                      notification.createdDate
                                    )}
                                    enterDelay={300}
                                    leaveDelay={0}
                                  >
                                    <Typography
                                      component="span"
                                      style={{
                                        fontSize: "12px",
                                        color: "#A3A3A3",
                                        fontWeight: 400,
                                        marginTop: 4,
                                        display: "block",
                                      }}
                                    >
                                      {formatNotificationTime(
                                        notification.createdDate
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            } else if (
                              isVideoRequestNotification &&
                              notification.type === "videoRequestSend" &&
                              notification.senderId !== userId &&
                              notification.videoRequest?.status !== "accepted" &&
                              notification.videoRequest?.status !== "rejected"
                            ) {
                              // Video request notifications - show message and action buttons
                              // Only show buttons for recipient (not for sender)
                              return (
                                <>
                                  {notification.senderId ? (
                                    <Link
                                      to={`/${notification.senderId}`}
                                      style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                      }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleProfileClick(e);
                                      }}
                                    >
                                      <Typography
                                        component="div"
                                        className={classes.headline}
                                        style={{ marginBottom: 4 }}
                                      >
                                        {notification.firstName &&
                                          notification.lastName
                                          ? `${notification.firstName} ${notification.lastName}`
                                          : notification.senderId || "User"}
                                        {hasFirstAndLastName && (
                                          <VerifiedShieldHelpIcon
                                            style={{ marginLeft: "1px" }}
                                            className={classes.verifiedIcon}
                                          />
                                        )}
                                      </Typography>
                                    </Link>
                                  ) : null}
                                  <Typography
                                    component="div"
                                    className={classes.body}
                                    onClick={handleContentClick}
                                    style={{
                                      cursor:
                                        notification.postId ||
                                          notification.senderId
                                          ? "pointer"
                                          : "default",
                                    }}
                                  >
                                    {notification.repeaterMessage ||
                                      notification.message}
                                  </Typography>
                                  <Box
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      marginTop: "12px",
                                    }}
                                  >
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={(
                                        event: React.MouseEvent<HTMLButtonElement>
                                      ) => {
                                        event.stopPropagation();
                                        handleVideoRequestAction(
                                          notification,
                                          "accept"
                                        );
                                      }}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor =
                                          "#FF6BA8";
                                        e.currentTarget.style.borderColor =
                                          "#FF6BA8";
                                      }}
                                      onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor =
                                          "#FE5A97";
                                        e.currentTarget.style.borderColor =
                                          "#FE5A97";
                                      }}
                                      style={{
                                        backgroundColor: "#FE5A97",
                                        border: "1px solid #FE5A97",
                                        color: "#FFFFFF",
                                        textTransform: "none",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        padding: "6px 14px",
                                        borderRadius: "8px",
                                        whiteSpace: "nowrap",
                                        minWidth: "auto",
                                        transition: "all 0.2s ease",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={(
                                        event: React.MouseEvent<HTMLButtonElement>
                                      ) => {
                                        event.stopPropagation();
                                        handleVideoRequestAction(
                                          notification,
                                          "reject"
                                        );
                                      }}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor =
                                          "#FE5A97";
                                        e.currentTarget.style.borderColor =
                                          "#FE5A97";
                                        e.currentTarget.style.color = "#FFFFFF";
                                      }}
                                      onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor =
                                          "transparent";
                                        e.currentTarget.style.borderColor =
                                          "#FE5A97";
                                        e.currentTarget.style.color = "#FE5A97";
                                      }}
                                      style={{
                                        backgroundColor: "transparent",
                                        border: "1px solid #FE5A97",
                                        color: "#FE5A97",
                                        textTransform: "none",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        padding: "6px 14px",
                                        borderRadius: "8px",
                                        whiteSpace: "nowrap",
                                        minWidth: "auto",
                                        transition: "all 0.2s ease",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </Box>
                                  <Tooltip
                                    title={formatNotificationTimeFull(
                                      notification.createdDate
                                    )}
                                    enterDelay={300}
                                    leaveDelay={0}
                                  >
                                    <Typography
                                      component="span"
                                      style={{
                                        fontSize: "12px",
                                        color: "#A3A3A3",
                                        fontWeight: 400,
                                        marginTop: 4,
                                        display: "block",
                                      }}
                                    >
                                      {formatNotificationTime(
                                        notification.createdDate
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            } else if (isPlatformUpdates) {
                              return (
                                <>
                                  <div className={classes.headline}>Platform Updates:</div>
                                  <Typography component="div" className={classes.body}>
                                    <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                                  </Typography>
                                </>
                              );
                            }
                            else if (isWeeklyTrending) {
                              const lines = (notification.repeaterMessage || notification.message)
                                .split("\n")
                                .filter(line => line.trim() !== "");

                              const header = lines[0];
                              const grouped = [];

                              for (let i = 1, postIndex = 0; i < lines.length; i += 2, postIndex++) {
                                const match = lines[i].match(/@([^\s—]+)/);
                                const username = match ? match[1] : null;

                                grouped.push({
                                  title: lines[i],
                                  stats: lines[i + 1],
                                  postId: notification?.postIds?.[postIndex],
                                  username
                                });
                              }
                              return (
                                <>
                                  <Typography component="div" className={classes.body}>
                                    <div>{header}</div>
                                    {grouped.map((item, idx) => (
                                      <div key={idx}>
                                        <Link
                                          to={{
                                            pathname: `/${item.username}`,
                                            search: `?postId=${item.postId}`,
                                            state: { fromNotification: true }
                                          }}
                                          style={{ textDecoration: "none", color: "inherit" }}
                                        >
                                          <div style={{ cursor: "pointer" }}>{item.title}</div>
                                          <div style={{ marginLeft: "16px", cursor: "pointer" }}>{item.stats}</div>
                                        </Link>
                                      </div>
                                    ))}
                                  </Typography>
                                </>
                              );

                            } else {
                              // Follow notifications
                              // Remove first name and last name from message if they exist
                              const cleanMessage = (() => {
                                let message = notification.message || "";
                                const fullName =
                                  notification.firstName && notification.lastName
                                    ? `${notification.firstName} ${notification.lastName}`
                                    : notification.firstName ||
                                    notification.lastName ||
                                    "";

                                if (fullName) {
                                  // Escape special regex characters in the name
                                  const escapedFullName = fullName.replace(
                                    /[.*+?^${}()|[\]\\]/g,
                                    "\\$&"
                                  );

                                  // Remove full name from anywhere in the message (not just beginning)
                                  // This handles cases like "🔥 Jade Cherry posted..." or "Jade Cherry posted..."
                                  message = message.replace(
                                    new RegExp(
                                      `\\s*${escapedFullName}\\s+`,
                                      "gi"
                                    ),
                                    " "
                                  );
                                  message = message.replace(
                                    new RegExp(`^${escapedFullName}\\s*`, "i"),
                                    ""
                                  );

                                  // Also try removing just first name or last name if full name wasn't found
                                  if (notification.firstName) {
                                    const escapedFirstName =
                                      notification.firstName.replace(
                                        /[.*+?^${}()|[\]\\]/g,
                                        "\\$&"
                                      );
                                    message = message.replace(
                                      new RegExp(
                                        `\\s*${escapedFirstName}\\s+`,
                                        "gi"
                                      ),
                                      " "
                                    );
                                    message = message.replace(
                                      new RegExp(`^${escapedFirstName}\\s+`, "i"),
                                      ""
                                    );
                                  }
                                  if (notification.lastName) {
                                    const escapedLastName =
                                      notification.lastName.replace(
                                        /[.*+?^${}()|[\]\\]/g,
                                        "\\$&"
                                      );
                                    message = message.replace(
                                      new RegExp(
                                        `\\s*${escapedLastName}\\s+`,
                                        "gi"
                                      ),
                                      " "
                                    );
                                    message = message.replace(
                                      new RegExp(`^${escapedLastName}\\s+`, "i"),
                                      ""
                                    );
                                  }
                                }
                                return message.trim();
                              })();

                              return (
                                <>
                                  <Link
                                    to={`/${notification.senderId}`}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleProfileClick(e);
                                    }}
                                  >
                                    <Typography
                                      component="div"
                                      className={classes.headline}
                                    >
                                      {notification.firstName}{" "}
                                      {notification.lastName}{" "}
                                      {hasFirstAndLastName && (
                                        <VerifiedShieldHelpIcon
                                          style={{ marginLeft: "1px" }}
                                          className={classes.verifiedIcon}
                                        />
                                      )}
                                    </Typography>
                                  </Link>
                                  <Typography
                                    component="div"
                                    className={classes.body}
                                    onClick={handleContentClick}
                                    style={{
                                      cursor:
                                        notification.postId ||
                                          notification.senderId
                                          ? "pointer"
                                          : "default",
                                    }}
                                  >
                                    {cleanMessage}
                                  </Typography>
                                  <Tooltip
                                    title={formatNotificationTimeFull(
                                      notification.createdDate
                                    )}
                                    enterDelay={300}
                                    leaveDelay={0}
                                  >
                                    <Typography
                                      component="span"
                                      style={{
                                        fontSize: "12px",
                                        color: "#A3A3A3",
                                        fontWeight: 400,
                                        marginTop: 4,
                                        display: "block",
                                      }}
                                    >
                                      {formatNotificationTime(
                                        notification.createdDate
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            }
                          })()}
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
              {/* Infinite scroll loader */}
              {hasMoreNotifications && (
                <div
                  ref={loadMoreNotificationsRef}
                  style={{ minHeight: loading ? "50px" : "20px" }}
                >
                  {loading && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      padding={1}
                      style={{ paddingTop: "8px", paddingBottom: "8px" }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ color: "#FE5A97" }}
                      >
                        <CircularProgress
                          size={20}
                          style={{ color: "#FE5A97" }}
                        />
                        <Typography
                          variant="caption"
                          style={{
                            color: "#A3A3A3",
                            fontSize: "12px",
                            marginLeft: "8px",
                          }}
                        >
                          Loading more notifications...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          )}
        </>
      )}

      {isFollowRequestsView && (
        <>
          {loadingRequests ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={4}
              minHeight="200px"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                style={{ gap: "16px" }}
              >
                <CircularProgress size={32} style={{ color: "#FE5A97" }} />
                <Typography
                  variant="body2"
                  style={{
                    color: "#A3A3A3",
                    fontSize: "14px",
                  }}
                >
                  Loading requests...
                </Typography>
              </Box>
            </Box>
          ) : !loadingRequests &&
            hasFetchedRequestsOnce &&
            pendingRequests.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ padding: "60px 40px" }}
            >
              <img
                src={noNotificationsIcon}
                alt="No follow requests"
                style={{
                  width: "120px",
                  height: "auto",
                  marginBottom: "20px",
                }}
              />
              <Typography
                style={{
                  fontSize: "20px",
                  fontWeight: 400,
                  color: "#FFFFFF",
                }}
              >
                No new notifications
              </Typography>
            </Box>
          ) : (
            <Box
              className={classes.listContainer}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                position: "relative",
              }}
            >
              <List
                dense
                style={{
                  transform:
                    pullToRefreshState.isPulling ||
                      pullToRefreshState.isRefreshing
                      ? `translateY(${Math.min(pullToRefreshState.pullDistance * 0.5, 50)}px)`
                      : "translateY(0px)",
                  transition: pullToRefreshState.isRefreshing
                    ? "transform 0.3s ease"
                    : "none",
                }}
              >
                {pendingRequests.map(request => (
                  <ListItem
                    key={request._id}
                    className={classes.notificationItem}
                    onClick={e => {
                      // Don't navigate if clicking on buttons
                      const target = e.target as HTMLElement;
                      if (
                        target.tagName === "BUTTON" ||
                        target.closest("button")
                      ) {
                        return;
                      }
                      // Navigate to profile
                      history.push(`/${request.username}`);
                    }}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    <Link
                      to={`/${request.username}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <Avatar
                        src={
                          loadingMedia[request.profileImg || ""]
                            ? undefined
                            : mediaUrls[request.profileImg || ""] ||
                            defaultAvatars
                        }
                        className={classes.avatar}
                      >
                        {loadingMedia[request.profileImg || ""] && (
                          <CircularProgress size={24} />
                        )}
                      </Avatar>
                    </Link>
                    <ListItemText
                      primary={
                        <Box>
                          <Link
                            to={`/${request.username}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={e => {
                              e.stopPropagation();
                            }}
                          >
                            <Typography
                              component="div"
                              className={classes.headline}
                            >
                              {request.firstName} {request.lastName}{" "}
                              {request.firstName && request.lastName && (
                                <VerifiedShieldHelpIcon
                                  style={{ marginLeft: "1px" }}
                                  className={classes.verifiedIcon}
                                />
                              )}
                            </Typography>
                          </Link>
                          <Typography component="span" className={classes.body}>
                            {request.message}
                          </Typography>
                          <Tooltip
                            title={formatNotificationTimeFull(
                              request.createdAt
                            )}
                            enterDelay={300}
                            leaveDelay={0}
                          >
                            <Typography
                              component="span"
                              style={{
                                fontSize: "12px",
                                color: "#A3A3A3",
                                fontWeight: 400,
                                marginTop: 4,
                                display: "block",
                              }}
                            >
                              {formatNotificationTime(request.createdAt)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <Box style={{ display: "flex", gap: "2px" }}>
                      <Button
                        variant="outlined"
                        className={classes.acceptButton}
                        size="small"
                        onClick={() =>
                          handleFollowRequest(request.requestId, "accept")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </Button>
                      <Button
                        variant="outlined"
                        className={classes.acceptButton}
                        size="small"
                        onClick={() =>
                          handleFollowRequest(request.requestId, "reject")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
              {/* Infinite scroll loader for follow requests */}
              {hasMoreRequests && (
                <div
                  ref={loadMoreRequestsRef}
                  style={{ minHeight: loadingRequests ? "50px" : "20px" }}
                >
                  {loadingRequests && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      padding={1}
                      style={{ paddingTop: "8px", paddingBottom: "8px" }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ color: "#FE5A97" }}
                      >
                        <CircularProgress
                          size={20}
                          style={{ color: "#FE5A97" }}
                        />
                        <Typography
                          variant="caption"
                          style={{
                            color: "#A3A3A3",
                            fontSize: "12px",
                            marginLeft: "8px",
                          }}
                        >
                          Loading more requests...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          )}
        </>
      )}

      {/* Video Request Modal */}
      <Dialog
        open={videoRequestModalOpen}
        onClose={() => {
          setVideoRequestModalOpen(false);
          // Remove requestId from URL params
          const url = new URL(window.location.href);
          url.searchParams.delete("requestId");
          window.history.pushState({}, "", url.toString());
        }}
        className={classes.videoRequestModal}
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            borderRadius: "12px",
            padding: window.innerWidth < 600 ? "20px 15px" : "40px",
            maxWidth: "550px",
            width: "100%",
            position: "relative",
          },
        }}
      >
        {/* Close button - top right */}
        <IconButton
          onClick={() => {
            setVideoRequestModalOpen(false);
            // Remove requestId from URL params
            const url = new URL(window.location.href);
            url.searchParams.delete("requestId");
            window.history.pushState({}, "", url.toString());
          }}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            color: "#FFFFFF",
            padding: "8px",
          }}
        >
          <X size={20} />
        </IconButton>

        {/* Deadline label and input */}
        <Typography className={classes.videoRequestLabel}>Deadline:</Typography>
        <Input
          className={classes.videoRequestInputField}
          value={deadline}
          onChange={e => setDeadline(e.target.value)}
          placeholder="Select deadline..."
          type="date"
          fullWidth
          style={{ marginBottom: "20px" }}
        />

        {/* From label and input */}
        <Typography className={classes.videoRequestLabel}>From:</Typography>
        <Input
          className={classes.videoRequestInputField}
          value={from}
          onChange={e => setFrom(e.target.value)}
          placeholder="Enter sender name..."
          disabled
          fullWidth
          style={{ marginBottom: "20px" }}
        />

        {/* Request label and input */}
        <Typography className={classes.videoRequestLabel}>Request:</Typography>
        <Input
          className={classes.videoRequestInputField}
          value={requestMessage}
          onChange={e => setRequestMessage(e.target.value)}
          placeholder="Enter request..."
          disabled
          fullWidth
        />

        {/* Upload Video button */}
        <input
          ref={videoFileInputRef}
          type="file"
          accept="video/*"
          onChange={handleVideoFileSelect}
          className={classes.videoFileInput}
          id="video-file-input"
        />

        {selectedVideoFile && videoPreviewUrl ? (
          <Box style={{ marginBottom: "16px" }}>
            <video
              src={videoPreviewUrl}
              controls
              style={{
                width: "100%",
                maxHeight: "300px",
                borderRadius: "8px",
                backgroundColor: "#1F1F1F",
                marginBottom: "12px",
              }}
            />
            <Button
              className={classes.videoRequestRejectButton}
              onClick={() => videoFileInputRef.current?.click()}
              fullWidth
              style={{ marginBottom: "12px" }}
            >
              Change
            </Button>
          </Box>
        ) : (
          <Button
            className={classes.videoRequestUploadButton}
            onClick={() => videoFileInputRef.current?.click()}
            fullWidth
            style={{ marginBottom: "12px" }}
          >
            <Upload size={18} style={{ marginRight: "8px" }} />
            Select Video
          </Button>
        )}

        {selectedVideoFile && (
          <Button
            className={classes.videoRequestUploadButton}
            onClick={handleUploadVideo}
            fullWidth
            style={{ marginBottom: "12px" }}
          >
            Submit
          </Button>
        )}

        {/* Reject button */}
        <Button
          className={classes.videoRequestRejectButton}
          onClick={handleRejectFromModal}
          fullWidth
        >
          Reject
        </Button>
      </Dialog>

      {/* Reject Success Modal - shared component */}
      <RejectVideoRequestSuccessModal
        open={rejectSuccessModalOpen}
        onClose={() => setRejectSuccessModalOpen(false)}
        buttonText="Continue to Sugar cribs"
        onButtonClick={() => {
          setRejectSuccessModalOpen(false);
          history.push("/");
        }}
        onCancelRejection={async () => {
          const requestId =
            selectedVideoRequest?.videoRequest?._id ||
            selectedVideoRequest?.videoRequestId ||
            selectedVideoRequest?._id;
          if (!requestId) return;
          try {
            await setVideoRequestPendingAPI(requestId);
            showToast("Rejection cancelled", "success");
            await refreshNotifications();
          } catch (err: any) {
            showToast(err?.message || "Failed to cancel rejection", "error");
          } finally {
            setRejectSuccessModalOpen(false);
          }
        }}
      />
    </Box>
  );
};

export default InAppNotifications;
